import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AuthProvider } from './components/auth/AuthProvider';
import { Layout } from './components/layout/Layout';
import { useAuthStore } from '@shared/store/authStore';
import { WelcomeScreen } from './components/WelcomeScreen';

// Pages
import { SimpleHomePage as HomePage } from './pages/SimpleHomePage';
import { RegisterPage } from './pages/RegisterPage';
import { ProfilePage } from './pages/ProfilePage';
import { ServicesPage } from './pages/ServicesPage';
import { BookingPage } from './pages/BookingPage';
import { MembershipPage } from './pages/MembershipPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { CourtsPage } from './pages/CourtsPage';
import { GroupLessonsPage } from './pages/GroupLessonsPage';
import { PersonalTrainingPage } from './pages/PersonalTrainingPage';
import { BeautyServicesPage } from './pages/BeautyServicesPage';
import { SpaServicesPage } from './pages/SpaServicesPage';
import RestaurantPage from './pages/RestaurantPage';
import { ClubCardsPage } from './pages/ClubCardsPage';
import AIAssistantPage from './pages/AIAssistantPage';
import SettingsPage from './pages/SettingsPage';
import { GuestLandingPage } from './pages/GuestLandingPage';

// Trainer pages
import { TrainerLayout } from './trainer-components/layout/TrainerLayout';
import { DashboardPage } from './trainer-components/pages/DashboardPage';
import TrainerSchedulePage from './trainer-components/pages/SchedulePage';
import TrainerProfilePage from './trainer-components/pages/ProfilePage';

// Waiter pages
import { WaiterLayout } from './waiter-components/layout/WaiterLayout';
import { WaiterDashboardPage } from './waiter-components/pages/WaiterDashboardPage';
import { QRScannerPage } from './waiter-components/pages/QRScannerPage';
import { ClientProfilePage as WaiterClientProfilePage } from './waiter-components/pages/ClientProfilePage';
import { WaiterMenuPage } from './waiter-components/pages/WaiterMenuPage';
import { WaiterOrderConfirmPage } from './waiter-components/pages/WaiterOrderConfirmPage';
import { OrderDetailPage as WaiterOrderDetailPage } from './waiter-components/pages/OrderDetailPage';

// Компонент для редиректа на основе роли
function RoleBasedRedirect() {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();

  // Если пользователь авторизован и находится на главной странице
  if (isAuthenticated && user && location.pathname === '/') {
    // Если тренер - редирект на /trainer/dashboard
    if (user.role === 'trainer') {
      console.log('🔄 [App] Редирект тренера на /trainer/dashboard');
      return <Navigate to="/trainer/dashboard" replace />;
    }
  }

  // Для клиентов показываем главную страницу
  return <Layout><HomePage /></Layout>;
}

function App() {
  const { isAuthenticated } = useAuthStore();
  const [showWelcome, setShowWelcome] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  // Waiter и trainer роуты не показывают WelcomeScreen
  const isSpecialRoute = window.location.pathname.startsWith('/waiter') || window.location.pathname.startsWith('/trainer');

  useEffect(() => {
    // Для спецроутов сразу скрываем WelcomeScreen
    if (isSpecialRoute) {
      setShowWelcome(false);
      return;
    }
    // Для авторизованных пользователей - автоматически скрываем через 3 секунды
    if (isAuthenticated && showWelcome) {
      const fadeTimer = setTimeout(() => {
        setFadeOut(true);
      }, 2700); // Начинаем fade-out за 300ms до скрытия

      const hideTimer = setTimeout(() => {
        setShowWelcome(false);
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isAuthenticated, showWelcome, isSpecialRoute]);

  // Показываем WelcomeScreen после logout (но не для спецроутов)
  useEffect(() => {
    if (!isAuthenticated && !isSpecialRoute) {
      setShowWelcome(true);
      setFadeOut(false);
    }
  }, [isAuthenticated, isSpecialRoute]);

  return (
    <AuthProvider>
      {/* Welcome Screen - показывается для всех при старте */}
      {showWelcome && (
        <div
          className={`transition-opacity duration-300 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
          style={{ position: 'fixed', inset: 0, zIndex: 200 }}
        >
          <WelcomeScreen
            isAuthenticated={isAuthenticated}
            onAutoHide={() => setShowWelcome(false)}
          />
        </div>
      )}

      {/* Main App - скрывается пока показан Welcome Screen для неавторизованных */}
      <div style={{ visibility: showWelcome && !isAuthenticated ? 'hidden' : 'visible' }}>
        <Routes>
          {/* Public routes */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/guest" element={<GuestLandingPage />} />

          {/* Trainer routes */}
          <Route path="/trainer/*" element={<TrainerLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="schedule" element={<TrainerSchedulePage />} />
            <Route path="profile" element={<TrainerProfilePage />} />
          </Route>

          {/* Waiter routes */}
          <Route path="/waiter/*" element={<WaiterLayout />}>
            <Route index element={<WaiterDashboardPage />} />
            <Route path="scan" element={<QRScannerPage />} />
            <Route path="client/:clientId" element={<WaiterClientProfilePage />} />
            <Route path="menu/:clientId" element={<WaiterMenuPage />} />
            <Route path="confirm" element={<WaiterOrderConfirmPage />} />
            <Route path="order/:orderId" element={<WaiterOrderDetailPage />} />
          </Route>

          {/* Client routes - wrapped in Layout */}
          <Route path="/" element={<RoleBasedRedirect />} />
          <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />
          <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
          <Route path="/services/:category" element={<Layout><ServicesPage /></Layout>} />
          <Route path="/booking" element={<Layout><BookingPage /></Layout>} />
          <Route path="/booking/:serviceId" element={<Layout><BookingPage /></Layout>} />
          <Route path="/membership" element={<Layout><MembershipPage /></Layout>} />
          <Route path="/notifications" element={<Layout><NotificationsPage /></Layout>} />
          <Route path="/courts" element={<Layout><CourtsPage /></Layout>} />
          <Route path="/group-lessons" element={<Layout><GroupLessonsPage /></Layout>} />
          <Route path="/personal-training" element={<Layout><PersonalTrainingPage /></Layout>} />
          <Route path="/beauty" element={<Layout><BeautyServicesPage /></Layout>} />
          <Route path="/restaurant" element={<Layout><RestaurantPage /></Layout>} />
          <Route path="/club-cards" element={<Layout><ClubCardsPage /></Layout>} />
          <Route path="/ai-assistant" element={<Layout><AIAssistantPage /></Layout>} />
          <Route path="/settings" element={<Layout><SettingsPage /></Layout>} />
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;